import moment from 'moment';
import { Inject } from '@odinjs/odin';

import Controller, { OnClick, OnMount, OnReadSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { applicationContext, CredentialsValidationError, CredentialsValidationSuccess, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, TextAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import { AdminByScheduleParams } from './administration-schedule-params';
import { AdminModalsFractionalDoseService } from '../../services/admin-modals-fractional-dose-service';
import { AdminModalsService } from '../../services/admin-modals-service';
import { ApplicationRoute } from '../../../../constants/routes';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { MedicationViewerParams } from '../medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import BaseController from '../../../BaseController';
import MedicationService, { AdministrationChemotherapyMedicationRequest, AllowedAdministerItemParams, ConsistAdministrationMarginInfoRequest, ConsistAdministrationMarginInfoResponse, CreateEventMedicationConferenceRequest, CreateFirstMedicationCheckRequest, CreatePrescriptionScheduleChangeRequest } from '../../services/medication-service';
import { SimpleDialog } from '../../../dialog-helper';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import { AdminScheduleType, ChemotherapyStatus, Conditionals, DisplayMessages, FirstMedicationCheckInfoResponse, MedicationStatus, Params, PrescriptionStatus, ResponseBodyProps, ScheduleMedicationDoseInfo, TimelineItemType } from './administration-schedule-types';
import './administration-schedule-controller.css';
import { TimeUtils } from '../../../../utils/time-utils';
import { DialogType, ModalType, SimpleDialogParams } from '../../../../interfaces/simple-dialog';
import { AdministrationByScheduleAliases as Alias, FragmentNames } from '../../../../codegen/index';
import BarcodeService from '../../../../services/barcode-service';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { Function1113Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import { YesNoType } from '../../../../utils/enum-utils';

interface JWTToken {
  [key: string]: unknown;
  sub: string;
}

@Controller({ component: Alias.ADMINISTRATION_BY_SCHEDULE_LAYOUT, fragment: FragmentNames.ADMINISTRATION_BY_SCHEDULE })
class AdministrationByScheduleController extends BaseController {

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_NAME_TEXT)
  private adminByScheduleMedicationNameApi!: TextAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_DATE_TIME_TEXT)
  private adminByScheduleMedicationDateApi!: TextAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_FORM)
  private adminByScheduleFormApi!: FormAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_NEW_DATE_INPUT)
  private adminByScheduleCurrentDateApi!: InputAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_UNITY_INPUT)
  private adminByScheduleUnityApi!: InputAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_UNITY_FIELD)
  private adminByScheduleUnityFieldApi!: FieldAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_QUANTITY_INPUT)
  private adminByScheduleQuantityApi!: InputAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_ADMIN_ROUTE_INPUT)
  private adminByScheduleAdminRouteApi!: InputAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_BUTTON)
  private adminByScheduleButtonApi!: ButtonAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_BARCODE_PANEL)
  private adminByScheduleBarcodePanelApi!: PanelAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_CARDS_ACCORDION_PANEL)
  private adminByScheduleAccordionCardPanelApi!: PanelAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_CARD_DECK)
  private adminByScheduleCardDeckApi!: DeckAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_BARCODE)
  private adminByScheduleBarcodeApi!: BarcodeAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_OBSERVATION_FIELD)
  private adminByScheduleObservationFieldApi!: FieldAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_OBSERVATION_TEXT_AREA)
  private adminByScheduleObservationApi!: TextAreaAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_DILUTION_FIELD)
  private adminByScheduleDilutionFieldApi!: FieldAPI;

  @Inject(Alias.ADMINISTRATION_BY_SCHEDULE_DILUTION_TEXT_AREA)
  private adminByScheduleDilutionApi!: TextAreaAPI;

  state = {
    firstChecked: 'CHECKED_FIRST',
    messagefirstChecked: 'MPC',
    messageSameUser: 'MMU'
  }


  createEventMedicationConference(): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const formValues = this.adminByScheduleFormApi.getRecord();
    const justification = formValues.justification;

    const request: CreateEventMedicationConferenceRequest = {
      changeStatus: 58,
      encounterNumber: Number(encounterId),
      changeDate: moment().format(ISO_FULL_DATE_FORMAT),
      glucoseProteinSequence: 0,
      internalProcessSequence: 0,
      itemCode: Number(timelineMedication.itemCode),
      itemSequence: Number(timelineMedication.itemSequence) || 1,
      itemType: timelineMedication.itemType,
      justifyDescription: justification ? String(justification) : '',
      pdoPrn: BooleanUtils.isPositiveValue(MedicationViewerParams.adminSchedule.pdoPrn),
      prescriptionNumner: timelineMedication.prescriptionNumber,
      scheduleSequence: Number(timelineMedication.id),
    }

    MedicationService.createEventMedicationConference(request);
  }

  private createPrescriptionEndpoint(request: CreatePrescriptionScheduleChangeRequest): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    MedicationService.createPrescriptionScheduleChange(request)
      .then((body) => {
        const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
        const message = body.messageDescriptionResponse;
        let administredMessage = MessageService.translate(DisplayMessages.ADMINISTRED_MESSAGE);

        if (message) {
          administredMessage = `${administredMessage}. ${message}`;
        }

        MedicationService.showToast(administredMessage, ToastLevel.SUCCESS);
        AdminModalsService.redirectToTimeline(FragmentNames.ADMINISTRATION_BY_SCHEDULE);
        applicationContext.redirect(redirectRoute, { encounterId, patientId });
        applicationContext.clearNavigationStack();
      }).catch((error) => {
        return error.body.errors.map((message: string) => {
          MedicationService.showToast(message, ToastLevel.ERROR);
          this.adminByScheduleButtonApi.setEnabled(true);
        });
      });
  }

  private createParamsRequest(administrationRouteType: string, dosePrescriptionQuantity?: number,
    unitMeasureCode?: string, uoMCodeQuantityAdministered?: string, userValidateCredentials?: string | null): CreatePrescriptionScheduleChangeRequest {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const param22 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY) as number;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const { dateTime, justification, observation, doseQuantity } = this.adminByScheduleFormApi.getRecord();
    const dateTimeFormated = moment(String(dateTime)).format(ISO_FULL_DATE_FORMAT);
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const establishmentCode = Number(foundationContext.getEstablishment()?.id);
    const justifyDescription = justification ? String(justification) : '';
    const observationDescription = observation ? String(observation) : '';
    const prescriptionNumber = MedicationViewerParams.adminSchedule.prescriptionNumber;
    const materialSequence = MedicationViewerParams.adminSchedule.materialSequence;
    const eventDate = dateTimeFormated.includes('T') ? dateTimeFormated : dateTimeCurrent;
    const intervalCode = timelineMedication.intervalCode && String(timelineMedication.intervalCode);
    const pdoPrn = BooleanUtils.isPositiveValue(MedicationViewerParams.adminSchedule.pdoPrn);
    const userName = userValidateCredentials?.trim() || foundationContext.getUser()?.username;

    return {
      administrationRouteType,
      componentsDescription: '',
      encounterNumber: Number(encounterId),
      establishmentCode,
      eventDate,
      functionCode: 9129,
      intervalCode,
      isGenerateAll: Conditionals.NEGATIVE,
      itemCode: String(timelineMedication.itemCode),
      itemDescription: timelineMedication.description,
      itemObservationDescription: '',
      itemQuantity: Number(dosePrescriptionQuantity),
      itemSequence: Number(materialSequence),
      itemType: timelineMedication.itemType,
      justifyDescription,
      messageDescriptionResponse: '',
      observationDescription,
      pdoPrn,
      prescriptionHourQuantity: Number(param22),
      prescriptionNumber: Number(prescriptionNumber),
      prescriptionsNumber: String(timelineMedication.prescriptionsNumber ?? prescriptionNumber),
      quantityAdministered: Number(doseQuantity),
      scheduleSequence: Number(timelineMedication.id),
      suspendPrescribedMaterial: Conditionals.NEGATIVE,
      unitMeasureCode: unitMeasureCode as string,
      uoMCodeQuantityAdministered: uoMCodeQuantityAdministered as string,
      userName: userName,
    }
  }

  createPrescriptionScheduleChange(userValidateCredentials: string | null): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const doseUnitMeasureCode = this.context.get('doseUnitMeasureCode');
    const dosePrescriptionQuantity = MedicationViewerParams.adminSchedule.dosePrescriptionQuantity;
    const administrationRouteType = timelineMedication.administrationRouteType ? timelineMedication.administrationRouteType : '';
    const unitMeasureCode = doseUnitMeasureCode ? String(doseUnitMeasureCode) : '';
    const uoMCodeQuantityAdministered = doseUnitMeasureCode ? String(doseUnitMeasureCode) : '';

    this.createPrescriptionEndpoint(this.createParamsRequest(administrationRouteType, dosePrescriptionQuantity, unitMeasureCode, uoMCodeQuantityAdministered, userValidateCredentials));
  }

  private timelineEventIsM(secondUser: string | null, optionStatus: string): Promise<FirstMedicationCheckInfoResponse | null> {
    const timelineEvent = TimelineContext.get();
    const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
    const option = this.getOptionForCheckMedication()
    const userName = foundationContext.getUser()?.username;

    const body = {
      itemCode: Number(timeLineEventMedication.itemCode),
      itemType: timeLineEventMedication.itemType,
      option: optionStatus ? optionStatus : option,
      prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber),
      scheduleSequence: Number(timeLineEventMedication.id),
      userName: secondUser ? secondUser : userName,
    }

    return HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
      .then(response => {
        return response.body as FirstMedicationCheckInfoResponse;
      });
  }

  getFirstMedicationCheckInfo(secondUser: string | null, optionStatus: string): Promise<FirstMedicationCheckInfoResponse | null> {
    const timelineEvent = TimelineContext.get();

    if (this.validTypeToDoubleCheck(timelineEvent.itemType)) {
      return this.timelineEventIsM(secondUser, optionStatus)
    }

    return Promise.resolve(null);
  }

  goToTimeline(): void {
    AdminModalsService.redirectToTimeline(FragmentNames.ADMINISTRATION_BY_SCHEDULE);
    applicationContext.popNavigationItem();
  }

  private validatingThenFirstCheckMed(firstCheck: boolean, secondCheck: boolean | undefined,
    thirdCheck: boolean, lastCheck: boolean, userValidateCredentials: string | null): void {
    if (firstCheck || secondCheck) {
      this.firstMedicationChecked();
      this.goToTimeline();
      return;
    }

    if (thirdCheck || (lastCheck && TimelineContext.get().isChemoOrdenDisp == 'S')) {
      const onlyMessage = TimelineItemType.MEDICATION;
      this.administrationChemotherapyMedication(onlyMessage);
      return;
    }
    if (lastCheck) {
      this.createEventAndPrescription(userValidateCredentials);
    }
  }

  private async medResponse(res: FirstMedicationCheckInfoResponse | null, firstCheck: boolean, secondCheck: boolean | undefined,
    thirdCheck: boolean, lastCheck: boolean, userValidateCredentials: string | null): Promise<void> {
    const response = res as FirstMedicationCheckInfoResponse;
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);

    this.adminByScheduleButtonApi.setEnabled(true);
    if (res?.responseDescription) {
      MedicationService.showToast(response.responseDescription, ToastLevel.ERROR);
      if (param5 !== Params.NOT_ALLOWED) {
        this.credentialValidationUser();
      }
      this.context.set({ secondTry: true });
      return;
    }

    this.validatingThenFirstCheckMed(firstCheck, secondCheck, thirdCheck, lastCheck, userValidateCredentials);
  }

  private verifyAndGetFirstMedCheck(userValidateCredentials: string, optionStatus: string, firstCheck: boolean, secondCheck: boolean | undefined, thirdCheck: boolean, lastCheck: boolean): void {
    this.getFirstMedicationCheckInfo(userValidateCredentials, optionStatus)
      .then((res) => {
        this.medResponse(res, firstCheck, secondCheck, thirdCheck, lastCheck, userValidateCredentials);
      }).catch((error) => {
        return error.body.errors.map((message: string) => {
          MedicationService.showToast(message, ToastLevel.ERROR);
          this.adminByScheduleButtonApi.setEnabled(true);
        });
      });
  }
  async returnFirstMedCheck(userValidateCredentials: string, optionStatus: string): Promise<void> {
    const param6 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.USES_MEDICATION_CHECKING_STEP);
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;
    const status = TimelineContext.get().status;
    const allMedicationsRead = this.context.get('allMedicationsRead');
    const isChemoFirstCheck = this.context.get('chemotherapyStatus') === ChemotherapyStatus.CHECKED_FIRST;

    const firstCheck = chemotherapyStatus === ChemotherapyStatus.PENDING && status !== MedicationStatus.CHECKED_FIRST && !isChemoFirstCheck;
    const secondCheck = isChemotherapy && !chemotherapyStatus && status !== MedicationStatus.CHECKED_FIRST && !isChemoFirstCheck;
    const thirdCheck = chemotherapyStatus === ChemotherapyStatus.STARTED || chemotherapyStatus === ChemotherapyStatus.PENDING && !isChemoFirstCheck;
    const lastCheck = param6 === Params.NOT_ALLOWED || allMedicationsRead;

    this.verifyAndGetFirstMedCheck(userValidateCredentials, optionStatus, firstCheck, secondCheck, thirdCheck, lastCheck);
  }

  validateCredentialsWithSuccess(event: CredentialsValidationSuccess): void {
    this.context.set({ isGoToTimeline: false });
    const payloadSymbol = Object.getOwnPropertySymbols(event.token).find(s => s.description === 'payload');
    const userValidateCredentials = payloadSymbol && ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol]
      ? ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol].sub
      : ' ';
    this.returnFirstMedCheck(userValidateCredentials, this.state.messageSameUser)
  }

  remainingTries = 3;

  validateCredentialsWithError(event: CredentialsValidationError): void {
    const { abort } = event;
    this.adminByScheduleButtonApi.setEnabled(true);

    MedicationService.showToast(DisplayMessages.ERROR_VALIDATE_LOGIN, ToastLevel.ERROR);

    if (this.remainingTries > 0) {
      this.remainingTries = this.remainingTries - 1;
    }

    if (this.remainingTries === 0) {
      abort();
    }
  }

  credentialValidationUser(): void {
    foundationContext.promptCredentialsValidation({
      onDismiss: () => {
        this.adminByScheduleButtonApi.setEnabled(true);
      },
      onError: (event) => this.validateCredentialsWithError(event),
      onSuccess: (event) => this.validateCredentialsWithSuccess(event),
      validateButtonExpression: MessageService.translate(DisplayMessages.VALIDATE_BUTTON_EXP),
    });
  }

  private firstMedicationCheckCreated(created: string): void {
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    if (created === Conditionals.POSITIVE) {
      MedicationService.showToast(DisplayMessages.FIRST_MED_CHECK_CREATED, ToastLevel.SUCCESS);
      this.adminByScheduleButtonApi.setValue(DisplayMessages.ADMINISTRATION);
      const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;
      if (isChemotherapy) {
        this.context.set({ chemotherapyStatus: ChemotherapyStatus.CHECKED_FIRST });
      }
      this.context.set({ isFirstChecked: true, medicationStatus: MedicationStatus.CHECKED_FIRST });
      AdminByScheduleParams.params = {
        iconType: this.state.firstChecked,
        isFirstChecked: PrescriptionStatus.CHECKED_FIRST,
      }

      if (param5 === Params.NOT_ALLOWED && param561 === Params.ALLOWED) {
        this.createEventMedicationConference();
        this.goToTimeline();
        return;
      }

      this.context.set({isGoToTimeline: true});
      this.credentialValidationUser();
      return;
    }

    MedicationService.showToast(DisplayMessages.FIRST_CHECK_NOT_POSSIBLE, ToastLevel.WARNING);
  }

  private createFirstMedicationCheck(request: CreateFirstMedicationCheckRequest): void {

    MedicationService.createFirstMedicationCheck(request)
      .then((res) => {
        this.firstMedicationCheckCreated(res.created);
      }).catch(response => {
        const body = response.body;
        const hasErrors = !!body?.errors;
        if (hasErrors) {
          body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
      });
  }

  firstMedicationChecked(): void {
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const { encounterNumber } = this.historyService.getCurrentRouteParams();
    const { justification } = this.adminByScheduleFormApi.getRecord();
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const allMedicationsRead = this.context.get('allMedicationsRead');

    const request: CreateFirstMedicationCheckRequest = {
      encounterNumber: encounterNumber as number,
      eventDate: dateTimeCurrent,
      itemType: timelineMedication.itemType,
      justificationDescription: justification ? String(justification) : '',
      scheduleSequence: timelineMedication.id,
    }

    if (allMedicationsRead || param3 === Params.NOT_ALLOWED) {
      this.createFirstMedicationCheck(request);
    }
  }

  private allowedPrescriptionMedicine(scheduleSequence: number, isFirstChecked: FirstMedicationCheckInfoResponse | null): void {
    MedicationService.consistProcessSchedule(scheduleSequence)
      .then(async () => {
        if (isFirstChecked) {
          this.firstMedicationChecked();
          return;
        }

        MedicationService.showToast(MessageService.translate(DisplayMessages.FIRST_CHECK_NOT_POSSIBLE), ToastLevel.WARNING);
        return;
      }).catch((error) => {
        return error.body.errors.map((message: string) => {
          MedicationService.showToast(message, ToastLevel.ERROR);
          this.adminByScheduleButtonApi.setEnabled(true);
        });
      });
  }
  async consistProcessSchedule(): Promise<void> {
    const emarTimelineEvent = TimelineContext.get();
    const param58 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.VERIFY_ADEP_PROCESS_LINK_TO_TIMETABLE_ON_CHANGE) as string
    const scheduleSequence = emarTimelineEvent.id;
    const isFirstChecked = await this.getFirstMedicationCheckInfo(null, this.state.messagefirstChecked);

    if (param58 === Params.ALLOWED && this.validTypeToDoubleCheck(emarTimelineEvent.itemType)) {
      this.allowedPrescriptionMedicine(Number(scheduleSequence), isFirstChecked);
    }

    if (isFirstChecked) {
      this.firstMedicationChecked();
      return;
    }

    MedicationService.showToast(MessageService.translate(DisplayMessages.FIRST_CHECK_NOT_POSSIBLE), ToastLevel.WARNING);
    return;
  }

  private async validConsistAdmMarginInfo(eventDate: string, hasAsEvent: string, justificationDescription: string): Promise<void> {
    const response = await this.sendConsistAdmMarginInfoRequest(
      eventDate,
      hasAsEvent,
      justificationDescription
    );

    if (response?.responseDescription) {
      MedicationService.showToast(response.responseDescription, ToastLevel.ERROR);
      this.adminByScheduleButtonApi.setEnabled(true);
      return;
    }

    this.consistProcessSchedule();
    return;
  }

  async consistAdministrationMarginInfo(): Promise<void> {
    const emarTimelineEvent = TimelineContext.get();
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    const { observation, justification, dateTime } = this.adminByScheduleFormApi.getRecord();
    const dateTimeFormated = moment(String(dateTime)).format(ISO_FULL_DATE_FORMAT);
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const itemDoubleCheck = PrescriptionStatus.CHECKED_FIRST;
    const checkIsValid = (param13 !== Params.DO_NOT_CONTROL && param13 !== Params.NOT_ALLOWED && emarTimelineEvent.itemStatus !== itemDoubleCheck) || (emarTimelineEvent.itemStatus !== this.state.firstChecked);
    const eventDate = dateTimeFormated.includes('T') ? dateTimeFormated : dateTimeCurrent;
    const hasAsEvent = observation || justification ? YesNoType.YES : YesNoType.NO;
    const justificationDescription = justification ? String(justification) : '';
    if (checkIsValid) {
      this.validConsistAdmMarginInfo(eventDate, hasAsEvent, justificationDescription);
      return;
    }

    this.consistProcessSchedule();
  }

  async doesAllowAdministerItem(): Promise<void> {
    const userName = foundationContext.getUser()?.username;
    const internalProcessSequence = TimelineContext.get().internalProcedureSequence;
    const itemCode = TimelineContext.get().itemCode;
    const itemType = TimelineContext.get().itemType;

    const params: AllowedAdministerItemParams = {
      internalProcessSequence,
      isPalm: Conditionals.POSITIVE,
      itemCode: itemCode,
      itemType,
      originProcedureType: 0,
      sectorCode: 0,
      userName,
    }

    MedicationService.doesAllowAdministerItem(params)
      .then((response) => {
        if (!response) {
          MedicationService.showToast(DisplayMessages.ADM_NOT_ALLOWED, ToastLevel.ERROR);
          return;
        }

        this.consistAdministrationMarginInfo();
      });
  }

  validateIsExistScheduleSuspended(): void {
    const scheduleMaterialSequence = TimelineContext.get().id;

    MedicationService.findScheduleSuspended(Number(scheduleMaterialSequence))
      .then((res) => {
        const materialSuspendedDate = res.materialSuspendedDate;
        const medicalSuspendedDate = res.medicalSuspendedDate;

        if (materialSuspendedDate || medicalSuspendedDate) {
          MedicationService.showToast(DisplayMessages.EMAR_COULD_NOT_MANAGE, ToastLevel.ERROR);
          this.adminByScheduleButtonApi.setEnabled(true);
          return;
        }

        this.doesAllowAdministerItem();
      }).catch((error) => {
        return error.body.errors.map((message: string) => {
          MedicationService.showToast(message, ToastLevel.ERROR);
          this.adminByScheduleButtonApi.setEnabled(true);
        });
      });
  }

  getMedicationDoseInfo(): void {
    const param4 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.AUTOMATICALLY_LOAD_DOSE_AND_UOM_INFO);
    const param633 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.CONVERT_MEDICATION_DOSE_WHEN_FRACTIONATED);
    const scheduleMaterialSequence = TimelineContext.get().id;
    const endPoint = param633 === Params.ALLOWED ? Endpoint.EMAR_GET_SCHEDULE_DOSE_CONVERTED_INFO : Endpoint.EMAR_GET_SCHEDULE_DOSE_INFO;

    HttpService.get(endPoint, { scheduleMaterialSequence }).then(response => {
      const responseBody = response.body as ScheduleMedicationDoseInfo;

      if (param4 === Params.ALLOWED) {
        this.adminByScheduleQuantityApi.setValue(String(responseBody.doseQuantity));
        this.adminByScheduleUnityApi.setValue(String(responseBody.doseUnitMeasureDescription));
        this.adminByScheduleAdminRouteApi.setValue(String(responseBody.admRouteDescription ?? ''));
        this.context.set({ doseUnitMeasureCode: responseBody.doseUnitMeasureCode });
      } else {
        this.adminByScheduleQuantityApi.setValue('');
        this.adminByScheduleQuantityApi.setEnabled(true);
        this.adminByScheduleUnityApi.setValue('');
        this.adminByScheduleUnityApi.setEnabled(true);
        this.adminByScheduleUnityFieldApi.setValidators([]);
        this.adminByScheduleAdminRouteApi.setValue(String(responseBody.admRouteDescription ?? ''));
      }
    }).catch((error) => {
      return error.body.errors.map((message: string) => {
        MedicationService.showToast(message, ToastLevel.ERROR);
        this.adminByScheduleButtonApi.setEnabled(true);
      });
    });
  }

  validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const itemStatus = TimelineContext.get().status;
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;

    if ((!isChemotherapy && (param3 === Params.NOT_ALLOWED || itemStatus === MedicationStatus.CHECKED_FIRST)) ||
    (isChemotherapy && (itemStatus === MedicationStatus.PENDING || MedicationStatus.CHECKED_FIRST) && param3 === Params.NOT_ALLOWED)) {
      this.context.set({ allMedicationsRead: true });
      this.adminByScheduleBarcodePanelApi.setVisible(false);
      this.adminByScheduleAccordionCardPanelApi.setVisible(false);
      this.adminByScheduleButtonApi.setEnabled(true);
    } else if (isChemotherapy && itemStatus === MedicationStatus.CHECKED_FIRST && param3 != Params.NOT_ALLOWED && param5 === Params.ALLOWED) {
      this.adminByScheduleAccordionCardPanelApi.setVisible(true);
      this.adminByScheduleBarcodePanelApi.setVisible(true);
      this.adminByScheduleButtonApi.setEnabled(false);
      this.resetAllMedicationsCard().then(() => {
        this.searchAllMedicationsPendding().then((responseBody) => {
          const { materialCode } = responseBody as ResponseBodyProps;

          this.context.set({ materialCode });
        })
      });
    } else {
      this.adminByScheduleButtonApi.setEnabled(false);

      this.resetAllMedicationsCard().then(() => {
        this.searchAllMedicationsPendding().then((responseBody) => {
          const { materialCode } = responseBody as ResponseBodyProps;

          this.context.set({ materialCode });
        })
      });
    }
  }

  discard(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  validateAdministrationChemotherapyMedication(barcode: string): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();

    const request = {
      administeredStatus: MedicationViewerParams.chemotherapy.isAdministered,
      barcode: barcode,
      encounterId,
      encounterIdSequence: MedicationViewerParams.chemotherapy.encounterIdSequence,
      groupingSequence: MedicationViewerParams.chemotherapy.groupingNumber,
      listSeqMaterialInfusion: MedicationViewerParams.chemotherapy.materialSequence,
      materialSequence: MedicationViewerParams.chemotherapy.materialSequence,
      orderSequence: MedicationViewerParams.chemotherapy.orderSequence,
      prescriptionNumber: MedicationViewerParams.chemotherapy.prescriptionNumber
    } as AdministrationChemotherapyMedicationRequest;

    MedicationService.validateChemotherapyAdministration(request)
      .then((res) => {
        if (res.message) {
          MedicationService.showToast(res.message, ToastLevel.ERROR);
          this.adminByScheduleBarcodeApi.setEnabled(true);
          this.adminByScheduleBarcodePanelApi.setVisible(true);
        } else {
          this.context.set({ allMedicationsRead: true });
          MedicationService.showToast(DisplayMessages.MEDICATION_READ_SUCCESS, ToastLevel.SUCCESS);
          this.adminByScheduleButtonApi.setEnabled(true);
          this.adminByScheduleBarcodeApi.close();
          this.adminByScheduleBarcodePanelApi.setVisible(false);
        }
      });
  }

  administrationChemotherapyConfirm(): void {
    const administrationAfterConfirm = PrescriptionStatus.ADMINISTERED;
    this.administrationChemotherapyMedication(administrationAfterConfirm);
  }

  handleConfirmChemotherapyAdministration(message: string): void {

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: DisplayMessages.WISH_CONTINUE,
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.discard() },
      onClickOk: () => { this.administrationChemotherapyConfirm(); },
      titleDialog: message,
      titleOnCancel: DisplayMessages.LABEL_NO,
      titleOnOk: DisplayMessages.LABEL_YES,
    }

    SimpleDialog.openDialog(dialogParams);
  }

  administrationChemotherapyMedication(type: string): void {
    const param23 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOW_CHECK_TO_START_AND_ALLOW_CHECK_TO_FINISH);
    const request = {
      encounterIdSequence: MedicationViewerParams.chemotherapy.encounterIdSequence,
      groupingSequence: MedicationViewerParams.chemotherapy.groupingNumber,
      isApplication: 'C',
      isType: type,
      materialSequence: MedicationViewerParams.chemotherapy.materialSequence,
      orderSequence: MedicationViewerParams.chemotherapy.orderSequence,
      prescriptionNumber: MedicationViewerParams.chemotherapy.prescriptionNumber,
    }

    MedicationService.administrationChemotherapyMedication(request)
      .then((res) => {
        let responseContainsErrorMessage = res.message;
        const response = res.message?.split('#') as string[];
        const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
        const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

        if (type === PrescriptionStatus.ADMINISTERED && res?.message?.includes('S$')) {
          responseContainsErrorMessage = '';
          res.message = res.message.split('S$')[1];
        }

        const toastMessage = param23 !== Params.NOT_ALLOWED && res.message ? String(res.message) : DisplayMessages.ADMINISTRED_MESSAGE;

        if (type === TimelineItemType.MEDICATION && responseContainsErrorMessage) {
          this.handleConfirmChemotherapyAdministration(response[1]);
          return;
        }
        if (responseContainsErrorMessage && type !== TimelineItemType.MEDICATION) {
          MedicationService.showToast(responseContainsErrorMessage, ToastLevel.ERROR);
          return;
        }
        if (!responseContainsErrorMessage) {
          MedicationService.showToast(toastMessage, ToastLevel.SUCCESS);
          AdminModalsService.redirectToTimeline(FragmentNames.ADMINISTRATION_BY_SCHEDULE);
          applicationContext.redirect(redirectRoute, { encounterId, patientId });
          applicationContext.clearNavigationStack();
        }
      });
  }

  validateIfBarcodeAndAccordionCardsIsVisibleChemotherapy(): void {
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    if (param3 != Params.NOT_ALLOWED) {
      this.adminByScheduleAccordionCardPanelApi.setVisible(false);
      this.adminByScheduleBarcodeApi.setFormatterByProperty('value', '');
      this.adminByScheduleBarcodePanelApi.setVisible(true);
      this.adminByScheduleButtonApi.setEnabled(false);
    } else {
      this.context.set({ allMedicationsRead: true });
      this.adminByScheduleBarcodePanelApi.setVisible(false);
      this.adminByScheduleAccordionCardPanelApi.setVisible(false);
      this.adminByScheduleButtonApi.setEnabled(true);
    }
  }

  private emarGetInfoFirstMedCheck(body: Record<string, Value>): Promise<boolean> {
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);

    return HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
      .then(response => {
        const responseBody = response.body as FirstMedicationCheckInfoResponse;

        if (responseBody?.responseDescription && body.option === this.state.messageSameUser) {
          MedicationService.showToast(responseBody?.responseDescription, ToastLevel.ERROR);
          this.context.set({ sameUserCantCheck: true })
          return false
        }
        const medicationStatus = this.context.get('medicationStatus')

        if (!responseBody?.responseDescription || param561 === Params.NOT_ALLOWED || medicationStatus === MedicationStatus.CHECKED_FIRST) {
          return false;
        }

        return true;
      });
  }

  private validateIfIsFirstChecked(): Promise<boolean> {
    const timelineEvent = TimelineContext.get();

    if (this.validTypeToDoubleCheck(timelineEvent.itemType)) {
      const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
      const option = timeLineEventMedication.status === this.state.firstChecked ? this.state.messageSameUser : this.state.messagefirstChecked;
      const userName = foundationContext.getUser()?.username;

      const body = {
        itemCode: Number(timeLineEventMedication.itemCode),
        itemType: timeLineEventMedication.itemType,
        option,
        prescriptionNumber: String(MedicationViewerParams.adminSchedule.prescriptionNumber),
        scheduleSequence: Number(timeLineEventMedication.id),
        userName,
      };

      return this.emarGetInfoFirstMedCheck(body);

    }

    return Promise.resolve(false);
  }

  resetOneMedicationsCard = (barcodeValue: string): void => {
    const emarTimelineEvent = TimelineContext.get();
    const prescriptionNumber = MedicationViewerParams.adminSchedule.prescriptionNumber;

    HttpService.put(Endpoint.EMAR_RESET_ONE_MEDICATIONS_CARD, {
      prescriptionNumber,
      scheduleCurrentSequence: Number(barcodeValue),
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: emarTimelineEvent.id,
    }).then((resp) => {
      const response = resp.body as Record<string, Value>[] as ResponseBodyProps;
      const message = response.message;

      MedicationService.showToast(String(message), ToastLevel.SUCCESS);

      this.searchAllMedicationsPendding().then((responseBody) => {
        const responseSchedule = responseBody as Record<string, Value>[] as ResponseBodyProps[];
        const totalRead = responseSchedule.filter(((item) => item.read === true));
        const withoutReading = responseSchedule.filter(((item) => item.read === false));

        if (withoutReading.length > 0) {
          this.adminByScheduleBarcodeApi.setEnabled(true);
          this.adminByScheduleButtonApi.setEnabled(false);
          this.context.set({ readPendding: false });
        }

        this.adminByScheduleCardDeckApi.setData(responseBody);
        AdminByScheduleParams.readParam = { noCardsRead: true };

        if (totalRead.length > 0) {
          AdminByScheduleParams.readParam = { noCardsRead: false };
        }
      }).catch((error) => {
        MedicationService.showToast(error.message, ToastLevel.ERROR);
        this.adminByScheduleButtonApi.setEnabled(true);
      });
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
      this.adminByScheduleButtonApi.setEnabled(true);
    });
  }

  readOneMedicationsCard(barcodeRead: string): Promise<void | Record<string, Value>[]> {
    const emarTimelineEvent = TimelineContext.get();

    const body = {
      barcode: barcodeRead,
      prescriptionNumber: MedicationViewerParams.adminSchedule.prescriptionNumber,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: Number(emarTimelineEvent.id),
    }

    return HttpService.post(Endpoint.EMAR_READ_ONE_MEDICATIONS_CARD, {}, { body }
    ).then((res) => {
      const responseBody = res.body as Record<string, Value>[];

      return responseBody;
    });
  }

  resetAllMedicationsCard(): Promise<void | Record<string, Value>[]> {
    const emarTimelineEvent = TimelineContext.get();

    return HttpService.put(Endpoint.EMAR_RESET_ALL_MEDICATIONS_CARD, {
      prescriptionNumber: MedicationViewerParams.adminSchedule.prescriptionNumber,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: emarTimelineEvent.id,
    }).then((res) => {
      const responseBody = res.body as Record<string, Value>[];

      return responseBody;
    });
  }

  private noReading(): void {
    this.adminByScheduleButtonApi.setEnabled(true);
    this.adminByScheduleBarcodeApi.setEnabled(false);
    this.context.set({ allMedicationsRead: true });
  }

  private lastCheckLength(responseBody: Record<string, Value>[]): void {
    if (responseBody?.length > 0) {
      this.adminByScheduleCardDeckApi?.setData(responseBody);
      this.adminByScheduleCardDeckApi?.setLoading(false);
    }
  }
  private checkReadingLength(responseBody: Record<string, Value>[]): void {
    const noReading = responseBody.filter(((item) => item.read === false));
    const reading = responseBody.filter(((item) => item.read === true));

    if (reading.length > 0) {
      AdminByScheduleParams.readParam = { noCardsRead: false };
    }

    if (responseBody?.length > 11) {
      this.adminByScheduleCardDeckApi?.setDone(false);
    }

    if (noReading.length === 0) {
      this.noReading();
    }

    this.lastCheckLength(responseBody);
  }

  searchAllMedicationsPendding = (): Promise<Record<string, Value>[]> => {
    const emarTimelineEvent = TimelineContext.get();
    const prescriptionNumber = MedicationViewerParams.adminSchedule.prescriptionNumber;

    return HttpService.get(Endpoint.EMAR_GET_MEDICATIONS_PENDING, {
      prescriptionNumber,
      scheduleDate: emarTimelineEvent.start,
      scheduleSequence: emarTimelineEvent.id,
    }).then((res) => {
      const responseBody = res.body as Record<string, Value>[];

      this.adminByScheduleCardDeckApi?.setDone(true);
      this.adminByScheduleCardDeckApi?.setLoading(true);

      this.checkReadingLength(responseBody)

      return responseBody;
    });
  }

  handleBarcodeClose = (): void => {
    this.adminByScheduleBarcodeApi.close();
  }

  checkActionChemotherapyAdministration(): void {
    const param23 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOW_CHECK_TO_START_AND_ALLOW_CHECK_TO_FINISH);
    const itemStatus = TimelineContext.get().status;
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const startChemo = chemotherapyStatus !== ChemotherapyStatus.STARTED &&
      param23 !== Params.NOT_ALLOWED && itemStatus === MedicationStatus.CHECKED_FIRST;
    const finishChemo = chemotherapyStatus === ChemotherapyStatus.STARTED && param23 === Params.ALLOWED;

    if (finishChemo) {
      this.adminByScheduleButtonApi.setValue(DisplayMessages.FINISH);
      return;
    }

    if (startChemo) {
      this.adminByScheduleButtonApi.setValue(DisplayMessages.START_CHEMOTHERAPY);
      return;
    }

  }

  handleModalAdminScheduleFractionalDose(currentBarcode: string): void {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const medicationViewerParams = MedicationViewerParams.adminSchedule;

    const paramsAdmFractionDose = {
      barcodeClose: this.handleBarcodeClose,
      currentBarcode,
      medicationOrSolution: AdminScheduleType.MEDICATION,
      prescriptionNumber: Number(medicationViewerParams?.prescriptionNumber),
      scheduleDate: String(medicationViewerParams?.medicationHour),
      scheduleSequenceNumber: Number(scheduleSequenceNumber),
      updatedCards: this.searchAllMedicationsPendding
    }

    AdminModalsFractionalDoseService.modalAdminScheduleFractionalDose(paramsAdmFractionDose);
  }

  toastType(message: string): ToastLevel {
    switch (message) {
      case ToastLevel.SUCCESS.toLocaleUpperCase():
        return ToastLevel.SUCCESS;
      case ToastLevel.ERROR.toLocaleUpperCase():
        return ToastLevel.ERROR;
      default:
        return ToastLevel.SUCCESS;
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_LAYOUT })
  onMountAdminScheduleBarcode(): void {
    AdminByScheduleParams.readParam = { noCardsRead: true };
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const param23 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOW_CHECK_TO_START_AND_ALLOW_CHECK_TO_FINISH) as string;
    const medicationStatus = TimelineContext.get().status;
    this.context.set({ medicationStatus });
    if (chemotherapyStatus === ChemotherapyStatus.STARTED && param23 === Params.ALLOWED) {
      this.adminByScheduleBarcodeApi.setOpen(true);
    }
  }

  @OnUnmount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_LAYOUT })
  onUnmountLayoutAdministration(): void {
    const isGoToTimeline = this.context.get('isGoToTimeline');
    if ( isGoToTimeline ) {
      this.goToTimeline();
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_NAME_TEXT })
  onMountAdminScheduleMedicationName(): void {
    const medicationName = MedicineAndSolutionsParams.params.dsMaterialId;

    this.adminByScheduleMedicationNameApi.setValue(String(medicationName));
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_DATE_TIME_TEXT })
  onMountAdminScheduleMedicationDate(): void {
    const medicationDate = MedicationViewerParams.adminSchedule?.medicationHour;
    const dateTimeFormated = moment(String(medicationDate)).format('L LTS');

    this.adminByScheduleMedicationDateApi.setValue(dateTimeFormated);
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_QUANTITY_INPUT })
  onMountAdminScheduleQuantity(event: MountEvent): void {
    this.getMedicationDoseInfo();

    const inputApi = event.target as InputAPI
    if (this.isAssociatedItems()) {
      inputApi.setEnabled(false)
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_NEW_DATE_INPUT })
  onMountAdminScheduleCurrentDate({ target }: EventData<InputAPI>): void {
    TimeUtils.setDateToInput(this.adminByScheduleCurrentDateApi);
    const canEditTime =
        FoundationUtils.findModuleParam(FunctionName.ADEP_1113,
            ADEP_E1113Parameters.ALLOWS_EDITING_THE_TIME_OF_PERFORMING_THE_ACTIONS) as string;
    target.setEnabled(BooleanUtils.isTrueValue(canEditTime));
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_OBSERVATION_TEXT_AREA })
  onMountAdminScheduleObservation(): void {
    const observationDescription = TimelineContext.get().observationDescription;

    if (observationDescription) {
      this.adminByScheduleObservationApi.setValue(observationDescription);
      return;
    }

    this.adminByScheduleObservationFieldApi.setVisible(false);
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_DILUTION_TEXT_AREA })
  onMountAdminScheduleDilution(): void {
    const dilution = MedicationViewerParams.adminSchedule?.dilutionDescription;

    if (dilution) {
      this.adminByScheduleDilutionApi.setValue(dilution);
      return;
    }

    this.adminByScheduleDilutionFieldApi.setVisible(false);
  }

  private handleMountChecks(isChemotherapy?: boolean, buttonNotAllowed?: boolean, buttonAllowed?: boolean): void {
    if (isChemotherapy && TimelineContext.get().isChemoOrdenDisp === 'S') {
      this.validateIfBarcodeAndAccordionCardsIsVisibleChemotherapy();
    } else {
      this.validateIfBarcodeAndAccordionCardsIsVisible();
    }

    this.checkActionChemotherapyAdministration();

    if (buttonNotAllowed) {
      this.adminByScheduleButtonApi.setValue(DisplayMessages.ADMINISTRATION);
      this.context.set({ isFirstChecked: true });
      return;
    }
    const medicationStatus = this.context.get('medicationStatus')

    if (buttonAllowed && medicationStatus !== MedicationStatus.CHECKED_FIRST) {
      this.adminByScheduleButtonApi.setValue(DisplayMessages.EMAR_FIRST_CHECK);
      this.context.set({ isFirstChecked: false });
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_BUTTON })
  async onMountAdminScheduleButton(): Promise<void> {
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;

    const statusItem = await this.validateIfIsFirstChecked();
    const buttonNotAllowed = param561 === Params.NOT_ALLOWED && !statusItem && chemotherapyStatus !== ChemotherapyStatus.STARTED;
    const buttonAllowed = param561 === Params.ALLOWED && statusItem;

    const sameUserCantCheck = this.context.get('sameUserCantCheck');
    if (sameUserCantCheck) {
      this.adminByScheduleButtonApi.setEnabled(false);
      this.adminByScheduleBarcodePanelApi.setVisible(false);
      this.adminByScheduleAccordionCardPanelApi.setVisible(false);
      return;
    }

    this.handleMountChecks(isChemotherapy, buttonNotAllowed, buttonAllowed)
  }

  private validateConditions(isChemotherapyPending: boolean, isChemotherapyChecked: boolean, notAllowedAndAllRead: boolean, isMedicationInvalid: boolean, statusItem: boolean): void {
    const administer = PrescriptionStatus.ADMINISTERED;
    const onlyMessage = TimelineItemType.MEDICATION;
    if (isChemotherapyPending) {
      if (FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK) == Params.ALLOWED && statusItem) {
        this.validationsUnlikeChemotherapy(statusItem);
      } else {
        if (TimelineContext.get().isChemoOrdenDisp === 'S') {
          this.administrationChemotherapyMedication(onlyMessage);
          return;
        }
        this.createEventMedicationConference();
        this.createPrescriptionScheduleChange(null);
      }
      return;
    }

    if (isChemotherapyChecked) {
      this.validateConditionsChemotherapyChecked(administer);
      return;
    }

    if (notAllowedAndAllRead) {
      if (isMedicationInvalid) {
        this.createEventMedicationConference();
        this.createPrescriptionScheduleChange(null);
        return;
      }
    }
  }

  private validateConditionsChemotherapyChecked(administer: string): void {
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    if (param5 === Params.ALLOWED) {
      this.context.set({isGoToTimeline: true});
      this.credentialValidationUser();
      this.createEventMedicationConference();
    } else {
      if (TimelineContext.get().isChemoOrdenDisp === 'S') {
        this.administrationChemotherapyMedication(administer);
        return;
      }
      this.createEventMedicationConference();
      this.createPrescriptionScheduleChange(null);
    }
  }

  private clickChemotherapy(isFirstChecked: boolean, statusItem: boolean): void {
    const param6 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.USES_MEDICATION_CHECKING_STEP);
    const isMedication = MedicationViewerParams.chemotherapy.isMedication;
    const chemotherapyStatus = MedicineAndSolutionsParams.params.chemotherapyStatus;
    const medicationStatus = TimelineContext.get().status;
    const allMedicationsRead = this.context.get('allMedicationsRead');

    const isChemotherapyPending = chemotherapyStatus === ChemotherapyStatus.PENDING;
    const isChemotherapyChecked = chemotherapyStatus === ChemotherapyStatus.STARTED ||
      (medicationStatus === MedicationStatus.CHECKED_FIRST && !isFirstChecked) || (medicationStatus === MedicationStatus.ADMINISTERED && chemotherapyStatus === ChemotherapyStatus.CHECKED_FIRST);
    const notAllowedAndAllRead = param6 === Params.NOT_ALLOWED || allMedicationsRead;
    const isMedicationInvalid = isMedication == null;

    this.validateConditions(isChemotherapyPending, isChemotherapyChecked, notAllowedAndAllRead, isMedicationInvalid, statusItem);
  }

  private async createEventAndPrescription(userValidateCredentials: string | null): Promise<void> {
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const { observation, justification, dateTime } = this.adminByScheduleFormApi.getRecord();
    const dateTimeFormatted = moment(String(dateTime)).format(ISO_FULL_DATE_FORMAT);
    const dateTimeCurrent = moment().format(ISO_FULL_DATE_FORMAT);
    const eventDate = dateTimeFormatted.includes('T') ? dateTimeFormatted : dateTimeCurrent;
    const hasAsEvent = observation || justification ? YesNoType.YES : YesNoType.NO;
    const justificationDescription = justification ? String(justification) : '';

    const response = await this.sendConsistAdmMarginInfoRequest(
      eventDate,
      hasAsEvent,
      justificationDescription
    );

    if (response?.responseDescription) {
      MedicationService.showToast(response.responseDescription, ToastLevel.ERROR);
      this.adminByScheduleButtonApi.setEnabled(true);
      return;
    }

    if (!(param5 === Params.NOT_ALLOWED && param561 === Params.ALLOWED)) {
      this.createEventMedicationConference();
    }

    this.createPrescriptionScheduleChange(userValidateCredentials);
    return;
  }

  private async sendConsistAdmMarginInfoRequest(
    eventDate: string,
    hasAsEvent: string,
    justificationDescription: string
  ): Promise<ConsistAdministrationMarginInfoResponse> {
    const emarTimelineEvent = TimelineContext.get();
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    const param12 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.MARGIN_IN_MINUTES_CHECKING_OUTSIDE_PRESCRIBED_HOURS) as number;
    const param507 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_IN_CASE_EXTRAPOLATION_CHECKING_MARGIN_PRIOR_START) as number;
    const itemType = TimelineContext.get().itemType;
    const justificationRequired = param13 === Function1113Param13Values.REQUIRED_JUSTIFICATION || param13 === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
    const requiresJustificationStatus =  justificationRequired ? YesNoType.YES : YesNoType.NO;

    const request: ConsistAdministrationMarginInfoRequest = {
      actionAdministrationOutsideMarginStatus: param13,
      eventDate,
      hasAsEvent,
      itemType,
      justificationDescription,
      minutesMarginBefore: param507 ? Number(param507) : 0,
      minutesQuantityMarginAdministration: Number(param12),
      requiresJustificationStatus,
      scheduleSequence: Number(emarTimelineEvent.id)
    };

    return MedicationService.consistAdministrationMarginInfoResponse(request);
  }

  private checkParamsChem(firstCheck: boolean, secondCheck: boolean, thirdCheck: boolean, lastCheck: boolean): void {
    if (firstCheck || secondCheck) {
      this.credentialValidationUser();
      return;
    }

    if (thirdCheck) {
      this.createEventAndPrescription(null);
      return;
    }

    if (lastCheck) {
      this.validateIsExistScheduleSuspended();
      return;
    }

    this.createEventAndPrescription(null);
  }

  private validationsUnlikeChemotherapy(statusItem: boolean): void {
    const medicationStatus = this.context.get('medicationStatus')
    const param561 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_MEDICATION_CHECK);
    const secondTry = this.context.get('secondTry');
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;

    const firstCheck = param5 !== Params.NOT_ALLOWED && medicationStatus === MedicationStatus.CHECKED_FIRST && !statusItem;
    const secondCheck = secondTry && param5 !== Params.NOT_ALLOWED;
    const thirdCheck = !isChemotherapy && medicationStatus === MedicationStatus.PENDING && !statusItem && !secondTry;
    const lastCheck = param561 === Params.ALLOWED && statusItem;

    this.checkParamsChem(firstCheck, secondCheck, thirdCheck, lastCheck);
  }

  private verifyOnClick(statusItem: boolean): void {
    const isFirstChecked = this.context.get('isFirstChecked');
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;

    this.adminByScheduleButtonApi.setEnabled(false);

    if (isChemotherapy) {
      this.clickChemotherapy(isFirstChecked, statusItem);
      return;
    }

    this.validationsUnlikeChemotherapy(statusItem);
    return;
  }

  @OnClick({ target: Alias.ADMINISTRATION_BY_SCHEDULE_BUTTON })
  async onClickAdminScheduleButton(): Promise<void> {
    const formIsValid = await this.adminByScheduleFormApi.validate();
    if (!formIsValid) {
      const allFieldsMustFilledIn = MessageService.translate('text.all-fields-must-filled-in')
      MedicationService.showToast(allFieldsMustFilledIn, ToastLevel.WARNING);
      return
    }

    const statusItem = await this.validateIfIsFirstChecked();
    this.verifyOnClick(statusItem);
  }

  private thenReadOneMedicationsCard(eventValue: string, responseBody: void | Record<string, Value>[]): void {
    const { totalRead, total, message, messageType, informDose } = responseBody as ResponseBodyProps;

    if (informDose) {
      this.handleModalAdminScheduleFractionalDose(eventValue);
      return;
    }

    if (!message) {
      MedicationService.showToast(MessageService.translate(DisplayMessages.MEDICATION_READ_SUCCESS), ToastLevel.SUCCESS);
    } else {
      MedicationService.showToast(String(message), this.toastType(String(messageType)));
    }

    this.adminByScheduleButtonApi.setEnabled(false);
    this.adminByScheduleBarcodeApi.setEnabled(true);
    this.context.set({ allMedicationsRead: false });

    this.searchAllMedicationsPendding();

    if (Number(totalRead) > 0) {
      AdminByScheduleParams.readParam = { noCardsRead: false };
    }

    if (total !== 0 && totalRead === total) {
      this.adminByScheduleButtonApi.setEnabled(true);
      this.adminByScheduleBarcodeApi.setEnabled(false);
      this.adminByScheduleBarcodeApi.close();
      this.context.set({ allMedicationsRead: true });
      return;
    }
  }
  private readInstChemotherapy(eventValue: string): void {
    this.readOneMedicationsCard(eventValue).then((responseBody) => {
      this.thenReadOneMedicationsCard(eventValue, responseBody);
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
      this.adminByScheduleButtonApi.setEnabled(true);
    });
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_BARCODE })
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess({ target: Alias.ADMINISTRATION_BY_SCHEDULE_BARCODE })
  onReadSuccess(event: ReadEvent<BarcodeData>): void {
    const { preventDefault } = event;
    const isChemotherapy = MedicationViewerParams.chemotherapy.isChemotherapy;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
    const eventValue = event.item.value.trim();

    if (isChemotherapy && TimelineContext.get().isChemoOrdenDisp === 'S') {
      this.validateAdministrationChemotherapyMedication(eventValue);
    } else {
      AdminByScheduleParams.readParam = { noCardsRead: true };

      this.readInstChemotherapy(eventValue);
    }
  }

  @OnClick({ target: Alias.ADMINISTRATION_BY_SCHEDULE_CARD })
  onClickResetButton(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence;

    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetOneMedicationsCard);
  }

  @OnMount([Alias.ADMINISTRATION_BY_SCHEDULE_NEW_DATE_FIELD,])
  handleMountEventDateField(event: MountEvent): void {
    const fieldApi = event.target as FieldAPI
    if (this.isAssociatedItems()) {
      fieldApi.setValidators([{ name: 'required' }])
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_QUANTITY_FIELD })
  handleMountQuantityField(event: MountEvent): void {
    const fieldApi = event.target as FieldAPI
    if (this.isAssociatedItems()) {
      fieldApi.removeValidator('required')
    }
  }

  @OnMount({ target: Alias.ADMINISTRATION_BY_SCHEDULE_UNITY_INPUT })
  handleMountUnityField(event: MountEvent): void {
    const inputApi = event.target as InputAPI
    if (this.isAssociatedItems()) {
      inputApi.setEnabled(false)
    }
  }

  private isAssociatedItems(): boolean {
    const { itemType } = TimelineContext.get() as TimeLineEventMedication;
    return itemType === PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS || itemType === PrescriptionType.GLYCEMIA_CONTROLS_ASSOCIATED_ITEMS
  }

  private getOptionForCheckMedication(): string {
    const timelineEvent = TimelineContext.get();
    const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
    return timeLineEventMedication.status === this.state.firstChecked ? this.state.messageSameUser : this.state.messagefirstChecked;
  }

  private validTypeToDoubleCheck(itemType: string): boolean {
    return [PrescriptionType.MEDICINE, PrescriptionType.GLYCEMIA_CONTROLS_ASSOCIATED_ITEMS, PrescriptionType.PROCEDURES_ASSOCIATED_ITEMS as string]
      .includes(itemType);
  }

}

export default AdministrationByScheduleController;
